import { useEffect, useMemo, useState } from "react";
import { api, API } from "../lib/api";
import { Film, Type, ImageIcon, AlertTriangle, Loader2 } from "lucide-react";
import { t as tt, fmtDateTime } from "../i18n";

// Galeria de uploads. Admin > Uploads: todos os usuários (sempre em português).
// Perfil > Uploads (mine): só os arquivos do próprio usuário, traduzido.
const DAY = 86400000;
const RECENT_DAYS = 11;
const abs = (u) => (u && u.startsWith("/api/") ? `${API}${u.replace(/^\/api/, "")}` : u);
const fmtSize = (b) => (!b ? "" : b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const KIND_LABEL = { image: "Imagens", video: "Vídeos", font: "Fontes" };

function Preview({ f }) {
  if (f.kind === "font") {
    const fam = `lgfx-up-${f.id}`;
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-secondary/40">
        {f.file_url && <style>{`@font-face{font-family:'${fam}';src:url('${abs(f.file_url)}');font-display:swap}`}</style>}
        <span className="text-4xl text-foreground" style={{ fontFamily: `'${fam}', sans-serif` }}>Aa</span>
        <span className="text-[10px] text-muted-foreground truncate max-w-[90%]">{f.family}</span>
      </div>
    );
  }
  if (f.thumb_url) return <img src={abs(f.thumb_url)} alt="" loading="lazy" className="w-full h-full object-contain" />;
  return (
    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
      {f.kind === "video" ? <Film className="h-8 w-8" /> : <ImageIcon className="h-8 w-8" />}
    </div>
  );
}

export function UploadsTab({ mine = false }) {
  const t = mine ? tt : (s, v) => (v ? s.replace(/\{(\w+)\}/g, (_, k) => v[k]) : s);
  const fmtDay = (iso) => { try { return mine ? fmtDateTime(iso, { dateStyle: "short" }) : new Date(iso).toLocaleDateString("pt-BR"); } catch { return ""; } };
  const [files, setFiles] = useState(null);
  const [loadErr, setLoadErr] = useState(null); // erro ao carregar (não confundir com "nenhum arquivo")
  const [reloadKey, setReloadKey] = useState(0);
  const [user, setUser] = useState("all");
  const [fmt, setFmt] = useState("all");
  const [period, setPeriod] = useState("all"); // all | recent | old

  useEffect(() => {
    setLoadErr(null);
    api.get(mine ? "/me/uploads" : "/admin/uploads")
      .then(({ data }) => setFiles(Array.isArray(data) ? data : []))
      .catch((e) => { setLoadErr(e?.response?.status ? `HTTP ${e.response.status}` : (e?.message || "erro de rede")); setFiles([]); });
  }, [mine, reloadKey]);

  const owners = useMemo(() => {
    const m = {};
    for (const f of files || []) if (!m[f.user_id]) m[f.user_id] = f.owner_name || f.owner_email || "—";
    return Object.entries(m).sort((a, b) => a[1].localeCompare(b[1]));
  }, [files]);
  const formats = useMemo(() => {
    const kinds = new Set(), exts = new Set();
    for (const f of files || []) { kinds.add(f.kind); if (f.ext) exts.add(f.ext); }
    return { kinds: [...kinds].filter((k) => KIND_LABEL[k]), exts: [...exts].sort() };
  }, [files]);

  const shown = (files || []).filter((f) => {
    if (user !== "all" && f.user_id !== user) return false;
    if (fmt !== "all") {
      if (fmt.startsWith("kind:") ? f.kind !== fmt.slice(5) : f.ext !== fmt) return false;
    }
    if (period !== "all") {
      const age = Date.now() - new Date(f.created_at).getTime();
      if (period === "recent" ? age > RECENT_DAYS * DAY : age <= RECENT_DAYS * DAY) return false;
    }
    return true;
  });

  const sel = "h-8 bg-input border border-border rounded-sm px-2 text-xs outline-none focus:border-primary";
  const seg = (v, label) => (
    <button onClick={() => setPeriod(v)} aria-pressed={period === v} data-testid={`uploads-period-${v}`}
      className={`px-2.5 h-7 rounded-sm text-[11px] ${period === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{label}</button>
  );

  if (files === null) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-3" data-testid="uploads-tab">
      <div className="flex flex-wrap items-center gap-2">
        {!mine && (
          <select className={sel} value={user} onChange={(e) => setUser(e.target.value)} aria-label="Filtrar por usuário" data-testid="uploads-filter-user">
            <option value="all">Todos os usuários</option>
            {owners.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        )}
        <select className={sel} value={fmt} onChange={(e) => setFmt(e.target.value)} aria-label={t("Filtrar por formato")} data-testid="uploads-filter-format">
          <option value="all">{t("Todos os formatos")}</option>
          {formats.kinds.map((k) => <option key={k} value={`kind:${k}`}>{t(KIND_LABEL[k])}</option>)}
          {formats.exts.length > 0 && <option disabled>──────</option>}
          {formats.exts.map((x) => <option key={x} value={x}>.{x}</option>)}
        </select>
        <div className="inline-flex items-center rounded-sm border border-border p-0.5 bg-input/40" role="group" aria-label={t("Filtrar por data")}>
          {seg("all", t("Todos"))}
          {seg("recent", t("Recentes (até {n} dias)", { n: RECENT_DAYS }))}
          {seg("old", t("Antigos (+{n} dias)", { n: RECENT_DAYS }))}
        </div>
        <span className="ml-auto text-[11px] text-muted-foreground" data-testid="uploads-count">{t("{a} de {b} arquivos", { a: shown.length, b: files.length })}</span>
      </div>

      {loadErr && (
        <div className="rounded-sm border border-destructive/60 bg-destructive/10 text-destructive text-xs p-3 flex items-center justify-between gap-3" data-testid="uploads-error">
          <span>{t("Não foi possível carregar os arquivos")} ({loadErr}).</span>
          <button onClick={() => { setFiles(null); setReloadKey((k) => k + 1); }} className="underline" data-testid="uploads-retry">{t("Tentar de novo")}</button>
        </div>
      )}
      {!loadErr && shown.length === 0 ? (
        <p className="text-center text-muted-foreground py-10 text-sm">{files.length ? t("Nenhum arquivo com esses filtros.") : t("Nenhum arquivo enviado ainda.")}</p>
      ) : (
        <div className={`grid grid-cols-2 sm:grid-cols-3 gap-3 ${mine ? "" : "lg:grid-cols-5"}`}>
          {shown.map((f) => (
            <a key={`${f.tipo}-${f.id}`} href={f.file_url ? abs(f.file_url) : undefined} target="_blank" rel="noopener noreferrer"
              title={t("Abrir o arquivo original")}
              className="group rounded-md border border-border/60 bg-card overflow-hidden hover:border-primary/50 transition-colors" data-testid={`upload-${f.id}`}>
              <div className="relative aspect-video checker-bg">
                <Preview f={f} />
                <span className="absolute top-1.5 left-1.5 text-[9px] font-mono uppercase bg-black/70 text-white rounded-sm px-1.5 py-0.5 flex items-center gap-1">
                  {f.kind === "font" ? <Type className="h-2.5 w-2.5" /> : f.kind === "video" ? <Film className="h-2.5 w-2.5" /> : <ImageIcon className="h-2.5 w-2.5" />}{f.ext}
                </span>
                {f.status && f.status !== "pronto" && (
                  <span className={`absolute top-1.5 right-1.5 text-[9px] rounded-sm px-1.5 py-0.5 flex items-center gap-1 ${f.status === "erro" ? "bg-destructive text-white" : "bg-amber-500 text-black"}`}>
                    {f.status === "erro" ? <AlertTriangle className="h-2.5 w-2.5" /> : <Loader2 className="h-2.5 w-2.5 animate-spin" />}{t(f.status)}
                  </span>
                )}
              </div>
              <div className="px-2 py-1.5 min-w-0">
                <p className="text-[11px] truncate" title={mine ? f.nome : `${f.nome} - ${f.owner_name || f.owner_email || "—"}`}>
                  <span className="text-foreground">{f.nome}</span>
                  {!mine && <span className="text-muted-foreground"> - {f.owner_name || f.owner_email || "—"}</span>}
                </p>
                <p className="text-[10px] text-muted-foreground truncate">{[fmtSize(f.tamanho_bytes), fmtDay(f.created_at)].filter(Boolean).join(" · ")}</p>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default UploadsTab;
